import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchMerchants } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import { scrollToElement } from 'core/utils/helpers/scrollToElement';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import DataTable from 'core/modules/DataTable/DataTable';
import MerchantExperienceLink from 'core/modules/MerchantExperienceLink/MerchantExperienceLink';
import MerchantRebate from 'core/modules/MerchantRebate/MerchantRebate';
import CategoryMerchantOffers from './components/CategoryMerchantOffers';
import Sort from '../Sort/Sort';

import './CategoryMerchantsTable.scss';

import {
  selectCategoryMerchantsTableConfig,
  selectCategoryMerchantsTableData,
  selectCategoryMerchantsTableIsLoaded,
  selectCategoryMerchantsTableIsLoading,
  selectCategoryMerchantsTableMetadata,
} from './selectors';

function CategoryMerchantsTable({ categoryName, category_id, namespace }) {
  const componentRef = useRef(null);
  const dispatch = useDispatch();

  const config = useSelector(selectCategoryMerchantsTableConfig);
  const isLoading = useSelector(selectCategoryMerchantsTableIsLoading);
  const isLoaded = useSelector(selectCategoryMerchantsTableIsLoaded);
  const merchants = useSelector(selectCategoryMerchantsTableData);
  const metadata = useSelector(selectCategoryMerchantsTableMetadata);

  const {
    api: {
      params,
      options,
    },
    rebateOptions,
    paginationOptions,
    sortOptions,
  } = config;

  const [sortByDefault, setSortByDefault] = useState(params.sort_by);
  const [sortTypeDefault, setSortTypeDefault] = useState(params.sort_type);
  const [limitItems, setLimitItems] = useState(params.limit);

  const scrollToComponentRef = () => {
    if (componentRef.current) {
      setTimeout(() => { scrollToElement(componentRef.current); }, 500);
    }
  };

  const callFetchMerchants = (additionalParams) => {
    const {
      limit, sort_by: sortBy, sort_type: sortType,
    } = additionalParams;

    if (limit) {
      setLimitItems(limit);
    }

    if (sortBy) {
      setSortByDefault(sortBy);
      setSortTypeDefault(sortType);
    }

    const paramsWithContentGroup = {
      category_id,
      ...params,
      limit: limit || limitItems,
      sort_by: sortBy || sortByDefault,
      sort_type: sortType || sortTypeDefault,
      ...additionalParams,
    };

    scrollToComponentRef();
    dispatch(fetchMerchants(paramsWithContentGroup, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      callFetchMerchants({});
    }
  });

  const getCategoryMerchantsTableContent = () => (
    merchants && merchants.map(merchant => (
      <div
        className="mn_categoryMerchantItem"
        key={merchant.name}
        data-test="category-merchants-item"
      >
        <div className="mn_merchantLinkAndRebateWrap">
          <MerchantExperienceLink merchant={merchant}>
            <div className="mn_logo">
              <span className="mn_sr-only">{merchant.name}</span>
              <img src={merchant.logoUrls._88x31} alt={merchant.name} />
            </div>
          </MerchantExperienceLink>
          <MerchantRebate {...merchant.rebate} {...rebateOptions} />
        </div>
        {
          merchant.offers &&
          merchant.offers.length > 0 &&
          <CategoryMerchantOffers offers={merchant.offers} categoryName={categoryName} merchantName={merchant.name} />
        }
      </div>
    )));

  if (isEmpty(merchants)) {
    return null;
  }

  const columns = [{
    Header: '',
    accessor: 'id',
  }];

  return (
    <div
      className="mn_categoryMerchantsTable"
      ref={componentRef}
      data-test="category-merchants-table"
    >
      <div className={`mn_content ${isLoading ? 'mn_contentIsLoading' : ''}`}>
        <Sort
          sortOptions={sortOptions}
          sortType="dropdown"
          fetchPlacementsMethod={callFetchMerchants}
        />
        <DataTable
          columns={columns}
          data={merchants}
          paginationOptions={paginationOptions}
          metadata={metadata}
          fetchPlacementsMethod={callFetchMerchants}
        >
          {getCategoryMerchantsTableContent()}
        </DataTable>
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><CategoryMerchantsTable {...props} /></ComponentErrorBoundary>;
